import React, { useState } from "react";
import { Button, Modal, Icon } from "semantic-ui-react";
import {
  Device,
  fetchAllDevices,
  fetchDevicesCount,
} from "../../../../../BytebeamClient";
import { MessageContainer } from "./BulkMetadataUpdateModal";
import csvjson from "csvjson";
import { saveAs } from "file-saver";
import LoadingAnimation from "../../../../common/Loader";
import { useUser } from "../../../../../context/User.context";
import { Permission } from "../../../../../util";

function replaceNullWithEmptyString(obj: Record<string, string | number>) {
  for (let key in obj) {
    if (obj[key] === null) {
      obj[key] = "";
    }
  }
}

// Returns a csv file content with selected device data based on checkboxes
export async function downloadDevicesData(
  devices: Device[],
  permissions: Permission,
  isMetadataSelected: boolean = true,
  isDeviceShadowDataSelected: boolean = false,
  isLastActionDataSelected: boolean = false,
  filterForEditMetadata: boolean = false
): Promise<string> {
  const allowedEditMetadataKeys = permissions.editMetadata;
  try {
    let selectedData: {
      id: number;
      Metadata?: Record<string, string | number>;
      State?: Record<string, string | number>;
      Action?: Record<string, string | number>;
    }[] = [];

    for (const device of devices) {
      const object: {
        id: number;
        Metadata?: Record<string, string | number>;
        State?: Record<string, string | number>;
        Action?: Record<string, string | number>;
      } = { id: device["id"] };

      // Handle metadata data if selected
      if (isMetadataSelected) {
        if (filterForEditMetadata && allowedEditMetadataKeys !== "all") {
          const filteredMetadata: Record<string, string | number> = {};
          for (const key of allowedEditMetadataKeys) {
            if (device["metadata"].hasOwnProperty(key)) {
              filteredMetadata[key] = device["metadata"][key];
            }
          }
          replaceNullWithEmptyString(filteredMetadata);
          object["Metadata"] = filteredMetadata;
        } else {
          replaceNullWithEmptyString(
            device["metadata"] as Record<string, string | number>
          );
          object["Metadata"] = device["metadata"] as Record<
            string,
            string | number
          >;
        }
      }

      // Handle state data (device shadow) if selected
      if (isDeviceShadowDataSelected) {
        if (device["state"] && Object.keys(device["state"]).length > 0) {
          const filteredState: Record<string, string | number> = {};
          for (const key in device["state"]) {
            if (!key.startsWith(".")) {
              filteredState[key] = device["state"][key];
            }
          }
          replaceNullWithEmptyString(filteredState);
          object["State"] = filteredState;
        } else {
          object["State"] = {};
        }
      }

      // Handle action data (last action) if selected
      if (isLastActionDataSelected) {
        if (device["action"] && Object.keys(device["action"]).length > 0) {
          replaceNullWithEmptyString(
            device["action"] as Record<string, string | number>
          );
          object["Action"] = device["action"] as Record<
            string,
            string | number
          >;
        } else {
          object["Action"] = {};
        }
      }

      selectedData.push(object);
    }

    let csvData: string = csvjson.toCSV(selectedData, { delimiter: "," });

    // To give a proper header to the csv file
    csvData = csvData.replace("[].", "");
    csvData = csvData.replaceAll("[].Metadata.", "");
    csvData = csvData.replaceAll("[].State.", "");
    csvData = csvData.replaceAll("[].Action.", "");
    return csvData;
  } catch (error) {
    console.error("Error While Fetching all devices selected data: ", error);
    throw error;
  }
}

type DownloadMetadataModalProps = {
  readonly close: (...args: any[]) => any;
  readonly isMetadataSelected: boolean;
  readonly isDeviceShadowDataSelected: boolean;
  readonly isLastActionDataSelected: boolean;
};

export function DownloadMetadataModal(props: DownloadMetadataModalProps) {
  const {
    close,
    isMetadataSelected = true,
    isDeviceShadowDataSelected = false,
    isLastActionDataSelected = false,
  } = props;
  const { user } = useUser();
  const permissions: Permission = user.role.permissions;

  const [buttonDisabled, setButtonDisabled] = useState(false);

  async function downloadAllDevicesData() {
    try {
      // Have to fetch total devices count again, because the props will have totalDevices count of the filtered selection
      let totalDevices = await fetchDevicesCount("active");
      // totalDevices is the possible maximum number of devices (totalDevices = page * devicesPerPage)
      const devices = await fetchAllDevices(1, totalDevices);
      const csvData: BlobPart = await downloadDevicesData(
        devices,
        permissions,
        isMetadataSelected,
        isDeviceShadowDataSelected,
        isLastActionDataSelected
      );
      setButtonDisabled(false);

      const file = new File([csvData], "device_data.csv", {
        type: "text/csv",
      });
      saveAs(file);
    } catch (error) {
      console.log("Error in downloading device data: ", error);
    }
  }

  return (
    <>
      <Modal.Content>
        <Modal.Description>
          <MessageContainer>
            {buttonDisabled ? (
              <LoadingAnimation
                loadingText="Downloading device data..."
                loaderBorderSize="3px"
                loaderSize="30px"
                fontSize="14px"
              />
            ) : (
              <p>
                Download{" "}
                {[
                  isMetadataSelected && "metadata",
                  isDeviceShadowDataSelected && "device shadow data",
                  isLastActionDataSelected && "last action data",
                ]
                  .filter(Boolean)
                  .join(", ")}{" "}
                for all devices by clicking on the <b>Download</b> button.
                Alternatively, click on the <b>Back</b> button to return to the
                previous screen.
              </p>
            )}
          </MessageContainer>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button
          secondary
          onClick={() => {
            close();
          }}
        >
          <Icon name="angle left" /> Back
        </Button>
        {/* disable the button unless parsing and fetching is done */}
        <Button
          primary
          disabled={buttonDisabled}
          onClick={async () => {
            setButtonDisabled(true);
            await downloadAllDevicesData();
            close();
          }}
        >
          <Icon name="checkmark" /> Download
        </Button>
      </Modal.Actions>
    </>
  );
}
